import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, DollarSign, ShoppingBag, Music, Mail, LogOut, 
  Upload, MessageSquare, Tag, Package, Album, Shirt, FileAudio,
  BarChart3, Users, Plus, Edit, Trash2, Send, Eye
} from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = '';

const AdminDashboard = () => {
  const { isAdminAuthenticated, adminLogout } = useAdmin();
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAdminAuthenticated) {
      navigate('/admin');
    } else {
      loadAnalytics();
    }
  }, [isAdminAuthenticated, navigate]);

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`/api/analytics`, {
        headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      // Set empty analytics to prevent crashes
      setAnalytics({
        inventory: { beats: 0, merch: 0, soundkits: 0, albums: 0 },
        subscribers: 0,
        payments: { total_transactions: 0, paid_transactions: 0, total_revenue: 0 },
        discount_codes: { total: 0, active: 0 },
        recent_transactions: []
      });
    }
  };

  const handleLogout = () => {
    adminLogout();
    toast({
      title: "Logged Out",
      description: "Successfully logged out of admin dashboard.",
    });
    navigate('/admin');
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'branding', label: 'Branding & Logos', icon: Upload },
    { id: 'beats', label: 'Beats', icon: Music },
    { id: 'merch', label: 'Merchandise', icon: Shirt },
    { id: 'soundkits', label: 'Sound Kits', icon: FileAudio },
    { id: 'albums', label: 'Albums', icon: Album },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'discounts', label: 'Discounts', icon: Tag },
    { id: 'orders', label: 'Orders', icon: ShoppingBag },
  ];

  return (
    <div className="min-h-screen bg-black pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Admin Dashboard</h1>
            <p className="text-gray-400">Manage your content and view analytics</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="text-white border-white/20 hover:bg-white/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Dropdown Menu */}
        <div className="mb-8">
          <label className="text-gray-400 text-sm mb-2 block">Select Section</label>
          <div className="relative">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full md:w-96 px-4 py-3 rounded-lg bg-zinc-900 border border-white/20 text-white font-bold appearance-none cursor-pointer hover:border-white/40 transition-all"
              style={{ 
                backgroundImage: `linear-gradient(45deg, transparent 50%, ${currentTheme.primary} 50%), linear-gradient(135deg, ${currentTheme.primary} 50%, transparent 50%)`,
                backgroundPosition: 'calc(100% - 20px) calc(1em + 2px), calc(100% - 15px) calc(1em + 2px)',
                backgroundSize: '5px 5px, 5px 5px',
                backgroundRepeat: 'no-repeat'
              }}
            >
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <option key={tab.id} value={tab.id} style={{ backgroundColor: '#18181b', color: '#ffffff' }}>
                    {tab.label}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="bg-zinc-900 rounded-2xl p-6 border border-white/10">
          {activeTab === 'dashboard' && <DashboardTab analytics={analytics} currentTheme={currentTheme} />}
          {activeTab === 'branding' && <BrandingTab currentTheme={currentTheme} />}
          {activeTab === 'beats' && <BeatsTab currentTheme={currentTheme} />}
          {activeTab === 'merch' && <MerchTab currentTheme={currentTheme} />}
          {activeTab === 'soundkits' && <SoundKitsTab currentTheme={currentTheme} />}
          {activeTab === 'albums' && <AlbumsTab currentTheme={currentTheme} />}
          {activeTab === 'customers' && <CustomersTab currentTheme={currentTheme} />}
          {activeTab === 'messages' && <MessagesTab currentTheme={currentTheme} />}
          {activeTab === 'email' && <EmailTab currentTheme={currentTheme} />}
          {activeTab === 'discounts' && <DiscountsTab currentTheme={currentTheme} />}
          {activeTab === 'orders' && <OrdersTab currentTheme={currentTheme} />}
        </div>
      </div>
    </div>
  );
};

// Dashboard Tab Component
const DashboardTab = ({ analytics, currentTheme }) => {
  if (!analytics) {
    return <div className="text-center text-gray-400 py-12">Loading analytics...</div>;
  }

  const metrics = [
    {
      label: 'Total Revenue',
      value: `$${analytics.payments.total_revenue}`,
      icon: DollarSign,
      color: '#10B981'
    },
    {
      label: 'Total Orders',
      value: analytics.payments.paid_transactions,
      icon: ShoppingBag,
      color: '#3B82F6'
    },
    {
      label: 'Beats',
      value: analytics.inventory.beats,
      icon: Music,
      color: currentTheme.primary
    },
    {
      label: 'Subscribers',
      value: analytics.subscribers,
      icon: Users,
      color: '#8B5CF6'
    },
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white mb-4">Overview</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div key={index} className="bg-black/50 rounded-xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${metric.color}20` }}
                >
                  <Icon className="w-6 h-6" style={{ color: metric.color }} />
                </div>
              </div>
              <div className="text-3xl font-bold text-white mb-1">{metric.value}</div>
              <div className="text-sm text-gray-400">{metric.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-black/50 rounded-xl p-6 border border-white/10">
          <h3 className="text-xl font-bold text-white mb-4">Inventory</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Beats</span>
              <span className="text-white font-bold">{analytics.inventory.beats}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Merchandise</span>
              <span className="text-white font-bold">{analytics.inventory.merch}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Sound Kits</span>
              <span className="text-white font-bold">{analytics.inventory.soundkits}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Albums</span>
              <span className="text-white font-bold">{analytics.inventory.albums}</span>
            </div>
          </div>
        </div>

        <div className="bg-black/50 rounded-xl p-6 border border-white/10">
          <h3 className="text-xl font-bold text-white mb-4">Discount Codes</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Total Codes</span>
              <span className="text-white font-bold">{analytics.discount_codes.total}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Active Codes</span>
              <span className="text-white font-bold" style={{ color: currentTheme.primary }}>
                {analytics.discount_codes.active}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Beats Management Tab
const BeatsTab = ({ currentTheme }) => {
  const [beats, setBeats] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBeat, setEditingBeat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    loadBeats();
  }, []);

  const loadBeats = async () => {
    setLoading(true);
    try {
      console.log('[BeatsTab] Loading beats from API...');
      const response = await axios.get(`/api/beats`);
      console.log('[BeatsTab] Received beats:', response.data.length);
      setBeats(response.data);
      toast({ 
        title: "Beats loaded", 
        description: `Found ${response.data.length} beat${response.data.length !== 1 ? 's' : ''}`,
        duration: 2000
      });
    } catch (error) {
      console.error('[BeatsTab] Failed to load beats:', error);
      toast({ title: "Error", description: "Failed to load beats: " + error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (beatId) => {
    if (!window.confirm('Are you sure you want to delete this beat? This action cannot be undone.')) {
      return;
    }

    setDeleting(beatId);
    try {
      await axios.delete(`/api/beats/${beatId}`, {
        headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
      });
      toast({ title: "Success", description: "Beat deleted successfully!" });
      loadBeats();
    } catch (error) {
      console.error('Failed to delete beat:', error);
      toast({ 
        title: "Error", 
        description: "Failed to delete beat: " + (error.response?.data?.detail || error.message), 
        variant: "destructive" 
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('⚠️ WARNING: This will delete ALL beats including uploaded files. Are you absolutely sure?')) {
      return;
    }
    if (!window.confirm('This is your last chance. Delete ALL beats?')) {
      return;
    }

    setLoading(true);
    try {
      // Delete each beat one by one
      for (const beat of beats) {
        await axios.delete(`/api/beats/${beat.id}`, {
          headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
        });
      }
      toast({ title: "Success", description: `Deleted ${beats.length} beats successfully!` });
      loadBeats();
    } catch (error) {
      console.error('Failed to delete beats:', error);
      toast({ title: "Error", description: "Failed to delete all beats", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Manage Beats ({beats.length})</h2>
          <p className="text-gray-400 text-sm">Upload, edit, and manage your beat library</p>
        </div>
        <div className="flex gap-2">
          {beats.length > 0 && (
            <Button
              onClick={handleDeleteAll}
              variant="destructive"
              className="text-white bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete All Beats
            </Button>
          )}
          <Button
            onClick={() => {
              setShowForm(!showForm);
              setEditingBeat(null);
            }}
            style={{ backgroundColor: currentTheme.primary }}
            className="text-black font-bold"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Beat
          </Button>
        </div>
      </div>

      {showForm && (
        <BeatForm 
          currentTheme={currentTheme} 
          beat={editingBeat}
          onSuccess={() => { 
            setShowForm(false); 
            setEditingBeat(null);
            // Add small delay to ensure database write completes before reloading
            setTimeout(() => {
              loadBeats();
            }, 500);
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingBeat(null);
          }}
        />
      )}

      {loading && !deleting ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-gray-600 border-t-white rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-4">Loading beats...</p>
        </div>
      ) : beats.length === 0 ? (
        <div className="text-center py-12 bg-black/50 rounded-xl border border-white/10">
          <Music className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No beats uploaded yet. Add your first beat to get started!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {beats.map(beat => (
            <div key={beat.id} className="bg-black/50 rounded-xl p-4 border border-white/10 hover:border-white/20 transition-all">
              <div className="flex items-center gap-4">
                <img 
                  src={beat.imageUrl || 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=200&h=200&fit=crop'} 
                  alt={beat.title} 
                  className="w-24 h-24 rounded-lg object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=200&h=200&fit=crop';
                  }}
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-bold text-lg truncate">{beat.title}</h3>
                  <p className="text-gray-400 text-sm">
                    {beat.genre} • {beat.bpm} BPM • {beat.key} • {beat.mood}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-white font-bold">${beat.price}</p>
                    {beat.featured && (
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-500 text-xs rounded">
                        Featured
                      </span>
                    )}
                  </div>
                  {beat.licenses && beat.licenses.length > 0 && (
                    <div className="flex gap-2 mt-2">
                      {beat.licenses.map((license, i) => (
                        <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded border border-blue-500/30">
                          {license.name}: ${license.price}
                        </span>
                      ))}
                    </div>
                  )}
                  {beat.tags && beat.tags.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {beat.tags.map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white/10 text-gray-300 text-xs rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-gray-400 hover:text-white"
                    onClick={() => {
                      setEditingBeat(beat);
                      setShowForm(true);
                    }}
                    title="Edit beat"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-400 hover:text-red-300"
                    onClick={() => handleDelete(beat.id)}
                    disabled={deleting === beat.id}
                    title="Delete beat"
                  >
                    {deleting === beat.id ? (
                      <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-1" />
                    )}
                    {deleting === beat.id ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Beat Upload/Edit Form
const BeatForm = ({ currentTheme, beat, onSuccess, onCancel }) => {
  const parseTagString = (value) => {
    if (!value) return [];
    return String(value)
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  };

  const [formData, setFormData] = useState({
    title: beat?.title || '',
    genre: beat?.genre || '',
    bpm: beat?.bpm || '',
    key: beat?.key || '',
    mood: beat?.mood || '',
    tags: beat?.tags ? beat.tags.join(', ') : '',
    featured: beat?.featured || false
  });
  const [genreInput, setGenreInput] = useState('');
  const [moodInput, setMoodInput] = useState('');
  const [mp3File, setMp3File] = useState(null);
  const [wavFile, setWavFile] = useState(null);
  const [trackoutsFile, setTrackoutsFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [licenses, setLicenses] = useState(beat?.licenses || [
    {
      name: "Basic License",
      price: 29.99,
      description: "MP3 • 2,500 streams • Tagged",
      features: ["MP3 Download", "Non-Profit Use", "2,500 Streaming Limit", "Untagged File Upon Purchase"],
      file_format: "MP3",
      allow_streaming: true,
      allow_profit: false,
      stream_limit: 2500,
      distribution_copies: 500
    },
    {
      name: "Premium License",
      price: 99.99,
      description: "MP3 & WAV • Unlimited streams • Untagged",
      features: ["MP3 Download", "WAV Download", "For-Profit Use", "Unlimited Streaming", "Music Video", "Radio Airplay"],
      file_format: "WAV",
      allow_streaming: true,
      allow_profit: true,
      stream_limit: null,
      distribution_copies: 5000
    },
    {
      name: "Exclusive Rights",
      price: "Contact",
      description: "Trackouts, WAVs and MP3s untagged",
      features: ["Full Ownership", "Trackout Files (Stems)", "WAV Files", "MP3 Files Untagged", "Unlimited Distribution", "Copyright Transfer", "Remove Beat from Sale"],
      file_format: "TRACKOUTS",
      allow_streaming: true,
      allow_profit: true,
      stream_limit: null,
      distribution_copies: null
    }
  ]);

  const isEditing = !!beat;
  const genreTags = parseTagString(formData.genre);
  const moodTags = parseTagString(formData.mood);

  const updateTagField = (field, values) => {
    const cleaned = values.map(item => item.trim()).filter(Boolean);
    setFormData(prev => ({ ...prev, [field]: cleaned.join(', ') }));
  };

  const addTag = (field, rawValue) => {
    const nextValue = rawValue.trim();
    if (!nextValue) return;

    const existingValues = parseTagString(formData[field]);
    const exists = existingValues.some(item => item.toLowerCase() === nextValue.toLowerCase());
    if (exists) return;

    updateTagField(field, [...existingValues, nextValue]);
  };

  const removeTag = (field, tagToRemove) => {
    const existingValues = parseTagString(formData[field]);
    updateTagField(field, existingValues.filter(item => item !== tagToRemove));
  };

  const handleTagKeyDown = (field, inputValue, setInputValue) => (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(field, inputValue);
      setInputValue('');
    } else if (e.key === 'Backspace' && !inputValue.trim()) {
      const existingValues = parseTagString(formData[field]);
      if (existingValues.length > 0) {
        removeTag(field, existingValues[existingValues.length - 1]);
      }
    }
  };

  const handleTagBlur = (field, inputValue, setInputValue) => () => {
    if (inputValue.trim()) {
      addTag(field, inputValue);
      setInputValue('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast({ title: "Error", description: "Beat title is required", variant: "destructive" });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const adminHeaders = { 'X-Admin-Password': 'advocate2026prizm' };
    const files = [
      { key: 'mp3_file', file: mp3File, label: 'MP3' },
      { key: 'wav_file', file: wavFile, label: 'WAV' },
      { key: 'trackouts_file', file: trackoutsFile, label: 'Trackouts' },
      { key: 'image_file', file: imageFile, label: 'Cover Image' },
    ].filter(f => f.file);

    const totalSteps = 1 + files.length;
    let currentStep = 0;

    try {
      // Step 1: Create/update beat with metadata + first small file (image or mp3)
      const data = new FormData();
      data.append('title', formData.title);
      if (formData.genre) data.append('genre', formData.genre);
      if (formData.bpm) data.append('bpm', formData.bpm);
      if (formData.key) data.append('key', formData.key);
      if (formData.mood) data.append('mood', formData.mood);
      data.append('featured', formData.featured);
      
      const tagsArray = formData.tags.split(',').map(t => t.trim()).filter(t => t);
      data.append('tags', JSON.stringify(tagsArray));
      data.append('licenses', JSON.stringify(licenses));

      // Attach only the image on initial create (small file)
      if (imageFile) {
        data.append('image_file', imageFile);
      }

      const url = isEditing ? `/api/beats/${beat.id}` : `/api/beats`;
      const method = isEditing ? 'put' : 'post';
      
      const response = await axios[method](url, data, {
        headers: { 'Content-Type': 'multipart/form-data', ...adminHeaders },
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(Math.round(pct / totalSteps));
        }
      });

      const beatId = response.data.id;
      currentStep = 1;
      setUploadProgress(Math.round((currentStep / totalSteps) * 100));

      // Step 2+: Upload each audio file — chunk if over 80MB for Cloudflare limit
      const CHUNK_SIZE = 80 * 1024 * 1024; // 80MB chunks
      const audioFiles = files.filter(f => f.key !== 'image_file');
      
      for (const fileInfo of audioFiles) {
        const fileSizeMB = (fileInfo.file.size / (1024*1024)).toFixed(1);
        toast({ title: `Uploading ${fileInfo.label}...`, description: `${fileInfo.file.name} (${fileSizeMB}MB)` });
        
        if (fileInfo.file.size <= CHUNK_SIZE) {
          // Small enough — single upload
          const fileData = new FormData();
          fileData.append('file', fileInfo.file);
          fileData.append('file_type', fileInfo.key);
          fileData.append('chunk_index', '0');
          fileData.append('total_chunks', '1');
          
          await axios.post(`/api/beats/${beatId}/upload-file`, fileData, {
            headers: { 'Content-Type': 'multipart/form-data', ...adminHeaders },
            timeout: 600000,
            onUploadProgress: (progressEvent) => {
              const filePct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              const overallPct = Math.round(((currentStep + filePct / 100) / totalSteps) * 100);
              setUploadProgress(overallPct);
            }
          });
        } else {
          // Large file — split into chunks
          const totalChunks = Math.ceil(fileInfo.file.size / CHUNK_SIZE);
          for (let i = 0; i < totalChunks; i++) {
            const start = i * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, fileInfo.file.size);
            const blob = fileInfo.file.slice(start, end);
            const chunkFile = new File([blob], fileInfo.file.name, { type: fileInfo.file.type });
            
            const fileData = new FormData();
            fileData.append('file', chunkFile);
            fileData.append('file_type', fileInfo.key);
            fileData.append('chunk_index', String(i));
            fileData.append('total_chunks', String(totalChunks));
            
            await axios.post(`/api/beats/${beatId}/upload-file`, fileData, {
              headers: { 'Content-Type': 'multipart/form-data', ...adminHeaders },
              timeout: 600000,
              onUploadProgress: (progressEvent) => {
                const chunkPct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                const chunkContrib = (i + chunkPct / 100) / totalChunks;
                const overallPct = Math.round(((currentStep + chunkContrib) / totalSteps) * 100);
                setUploadProgress(overallPct);
              }
            });
          }
        }

        currentStep++;
        setUploadProgress(Math.round((currentStep / totalSteps) * 100));
      }
      
      toast({ 
        title: "Success! 🎵", 
        description: isEditing ? "Beat updated successfully!" : "Beat uploaded successfully!" 
      });
      onSuccess();
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Upload failed';
      toast({ 
        title: "Error", 
        description: `Upload failed: ${errorMsg}`, 
        variant: "destructive" 
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-black/50 rounded-xl p-6 border border-white/10 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-gray-400 text-sm mb-2 block">Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
            required
          />
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-2 block">Genre</label>
          <div className="w-full px-3 py-3 rounded-lg bg-black/50 border border-white/10 text-white min-h-[48px]">
            <div className="flex flex-wrap gap-2 mb-2">
              {genreTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => removeTag('genre', tag)}
                  className="px-2.5 py-1 rounded-full text-xs font-semibold text-black"
                  style={{ backgroundColor: currentTheme.primary }}
                  title={`Remove ${tag}`}
                >
                  {tag} ×
                </button>
              ))}
            </div>
            <input
              type="text"
              value={genreInput}
              onChange={(e) => setGenreInput(e.target.value)}
              onKeyDown={handleTagKeyDown('genre', genreInput, setGenreInput)}
              onBlur={handleTagBlur('genre', genreInput, setGenreInput)}
              className="w-full bg-transparent text-white outline-none placeholder-gray-500"
              placeholder="Type a genre and press Enter or comma"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Add as many genres as you want — including custom ones you make up.</p>
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-2 block">BPM</label>
          <input
            type="number"
            value={formData.bpm}
            onChange={(e) => setFormData({...formData, bpm: e.target.value})}
            className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
            placeholder="140"
          />
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-2 block">Key</label>
          <input
            type="text"
            value={formData.key}
            onChange={(e) => setFormData({...formData, key: e.target.value})}
            className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
            placeholder="Am, C#, etc."
          />
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-2 block">Mood</label>
          <div className="w-full px-3 py-3 rounded-lg bg-black/50 border border-white/10 text-white min-h-[48px]">
            <div className="flex flex-wrap gap-2 mb-2">
              {moodTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => removeTag('mood', tag)}
                  className="px-2.5 py-1 rounded-full text-xs font-semibold text-black"
                  style={{ backgroundColor: currentTheme.primary }}
                  title={`Remove ${tag}`}
                >
                  {tag} ×
                </button>
              ))}
            </div>
            <input
              type="text"
              value={moodInput}
              onChange={(e) => setMoodInput(e.target.value)}
              onKeyDown={handleTagKeyDown('mood', moodInput, setMoodInput)}
              onBlur={handleTagBlur('mood', moodInput, setMoodInput)}
              className="w-full bg-transparent text-white outline-none placeholder-gray-500"
              placeholder="Type a mood and press Enter or comma"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Custom moods are good too — make up whatever matches the vibe.</p>
        </div>
      </div>

      <div>
        <label className="text-gray-400 text-sm mb-2 block">Tags (comma separated)</label>
        <input
          type="text"
          value={formData.tags}
          onChange={(e) => setFormData({...formData, tags: e.target.value})}
          placeholder="trap, dark, 808"
          className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
        />
      </div>

      {/* File Upload Section */}
      <div className="border-t border-white/10 pt-4 mt-4">
        <h3 className="text-white font-bold text-lg mb-3">Upload Files</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-gray-400 text-sm mb-2 block">MP3 File</label>
            <input
              type="file"
              accept=".mp3"
              onChange={(e) => setMp3File(e.target.files[0])}
              className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm"
            />
            {beat?.mp3Url && <p className="text-xs text-green-500 mt-1">✓ MP3 already uploaded</p>}
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-2 block">WAV File</label>
            <input
              type="file"
              accept=".wav"
              onChange={(e) => setWavFile(e.target.files[0])}
              className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm"
            />
            {beat?.wavUrl && <p className="text-xs text-green-500 mt-1">✓ WAV already uploaded</p>}
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-2 block">Trackouts (ZIP)</label>
            <input
              type="file"
              accept=".zip"
              onChange={(e) => setTrackoutsFile(e.target.files[0])}
              className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm"
            />
            {beat?.trackoutsUrl && <p className="text-xs text-green-500 mt-1">✓ Trackouts already uploaded</p>}
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-2 block">Cover Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])}
              className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm"
            />
            {beat?.imageUrl && <p className="text-xs text-green-500 mt-1">✓ Image already uploaded</p>}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.featured}
          onChange={(e) => setFormData({...formData, featured: e.target.checked})}
          className="w-4 h-4"
        />
        <label className="text-gray-400 text-sm">Featured Beat</label>
      </div>

      {/* Licensing Options Section */}
      <div className="border-t border-white/10 pt-4 mt-4">
        <h3 className="text-white font-bold text-lg mb-3">Licensing Options</h3>
        <p className="text-gray-400 text-sm mb-4">Configure the 3 licensing tiers for this beat</p>
        
        <div className="space-y-4">
          {licenses.map((license, index) => (
            <div key={index} className="bg-zinc-900 rounded-lg p-4 border border-white/10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">License Name</label>
                  <input
                    type="text"
                    value={license.name}
                    onChange={(e) => {
                      const newLicenses = [...licenses];
                      newLicenses[index].name = e.target.value;
                      setLicenses(newLicenses);
                    }}
                    className="w-full px-3 py-2 rounded bg-black/50 border border-white/10 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Price ($ or "Contact")</label>
                  <input
                    type="text"
                    value={license.price}
                    onChange={(e) => {
                      const newLicenses = [...licenses];
                      const val = e.target.value;
                      newLicenses[index].price = isNaN(val) || val === '' ? val : parseFloat(val);
                      setLicenses(newLicenses);
                    }}
                    placeholder="29.99 or Contact"
                    className="w-full px-3 py-2 rounded bg-black/50 border border-white/10 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Format</label>
                  <select
                    value={license.file_format}
                    onChange={(e) => {
                      const newLicenses = [...licenses];
                      newLicenses[index].file_format = e.target.value;
                      setLicenses(newLicenses);
                    }}
                    className="w-full px-3 py-2 rounded bg-black/50 border border-white/10 text-white text-sm"
                  >
                    <option value="MP3">MP3</option>
                    <option value="WAV">WAV</option>
                    <option value="TRACKOUTS">TRACKOUTS</option>
                  </select>
                </div>
              </div>
              <div className="mt-2">
                <label className="text-gray-400 text-xs mb-1 block">Description</label>
                <input
                  type="text"
                  value={license.description}
                  onChange={(e) => {
                    const newLicenses = [...licenses];
                    newLicenses[index].description = e.target.value;
                    setLicenses(newLicenses);
                  }}
                  className="w-full px-3 py-2 rounded bg-black/50 border border-white/10 text-white text-sm"
                  placeholder="e.g., MP3 • 2,500 streams • Tagged"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {uploading && uploadProgress > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-400">
            <span>Uploading...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="h-2 rounded-full transition-all duration-300"
              style={{ 
                width: `${uploadProgress}%`,
                backgroundColor: currentTheme.primary 
              }}
            />
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <Button
          type="submit"
          disabled={uploading}
          style={{ backgroundColor: currentTheme.primary }}
          className="flex-1 text-black font-bold"
        >
          <Upload className="w-4 h-4 mr-2" />
          {uploading ? `${isEditing ? 'Updating' : 'Uploading'}... ${uploadProgress}%` : isEditing ? 'Update Beat' : 'Upload Beat'}
        </Button>
        <Button
          type="button"
          onClick={onCancel}
          variant="outline"
          disabled={uploading}
          className="text-white border-white/20 hover:bg-white/10"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Branding & Logo Management Tab
const BrandingTab = ({ currentTheme }) => {
  const [branding, setBranding] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBranding();
  }, []);

  const loadBranding = async () => {
    try {
      const response = await axios.get(`/api/branding/settings`);
      setBranding(response.data);
    } catch (error) {
      console.error('Failed to load branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (logoType, file) => {
    if (!file) {
      toast({ title: "Error", description: "No file selected", variant: "destructive" });
      return;
    }

    console.log(`Uploading ${logoType} logo:`, file.name, file.size, file.type);
    setUploading(logoType);
    
    const formData = new FormData();
    formData.append('logo_type', logoType);
    formData.append('logo_file', file);

    try {
      const response = await axios.post(`/api/branding/upload-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm'
        },
        timeout: 30000 // 30 second timeout
      });
      
      console.log('Upload response:', response.data);
      toast({ title: "Success!", description: `${logoType} logo uploaded successfully!` });
      await loadBranding();
    } catch (error) {
      console.error('Upload error details:', error.response || error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast({ 
        title: "Upload Failed", 
        description: `Could not upload logo: ${errorMsg}. Check console for details.`, 
        variant: "destructive" 
      });
    } finally {
      setUploading(null);
    }
  };

  const handleDeleteLogo = async (logoType) => {
    if (!window.confirm(`Delete ${logoType} logo?`)) return;

    try {
      await axios.delete(`/api/branding/logo/${logoType}`, {
        headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
      });
      toast({ title: "Success", description: "Logo deleted successfully!" });
      loadBranding();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete logo", variant: "destructive" });
    }
  };

  const LogoUploadCard = ({ logoType, title, description, currentUrl }) => (
    <div className="bg-black/50 rounded-xl p-6 border border-white/10">
      <h3 className="text-white font-bold mb-2">{title}</h3>
      <p className="text-gray-400 text-sm mb-4">{description}</p>
      
      {currentUrl && (
        <div className="mb-4 p-4 bg-black/50 rounded-lg border border-white/10">
          <img 
            src={`${BACKEND_URL}${currentUrl}`} 
            alt={title}
            className="max-h-20 mx-auto object-contain"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/200x80?text=Logo';
            }}
          />
        </div>
      )}

      <div className="flex gap-2">
        <label className="flex-1 cursor-pointer">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                handleLogoUpload(logoType, file);
              }
              e.target.value = ''; // Reset input
            }}
            className="hidden"
            disabled={uploading === logoType}
          />
          <div
            style={{ backgroundColor: currentTheme.primary }}
            className="w-full px-4 py-2 rounded-lg text-black font-bold text-center transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center"
          >
            {uploading === logoType ? (
              <>
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </>
            )}
          </div>
        </label>
        {currentUrl && (
          <button
            onClick={() => handleDeleteLogo(logoType)}
            disabled={uploading === logoType}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-gray-600 border-t-white rounded-full animate-spin"></div>
        <p className="text-gray-400 mt-4">Loading branding settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Branding & Logo Management</h2>
        <p className="text-gray-400">Upload and manage your brand logos across the entire website</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <LogoUploadCard
          logoType="main"
          title="Main Logo"
          description="Primary logo used in navbar and hero section"
          currentUrl={branding?.logo_url}
        />
        
        <LogoUploadCard
          logoType="light"
          title="Light Logo"
          description="Logo for dark backgrounds (optional)"
          currentUrl={branding?.logo_light_url}
        />
        
        <LogoUploadCard
          logoType="dark"
          title="Dark Logo"
          description="Logo for light backgrounds (optional)"
          currentUrl={branding?.logo_dark_url}
        />
        
        <LogoUploadCard
          logoType="footer"
          title="Footer Logo"
          description="Logo displayed in website footer"
          currentUrl={branding?.footer_logo_url}
        />
        
        <LogoUploadCard
          logoType="favicon"
          title="Favicon"
          description="Small icon shown in browser tab (16x16 or 32x32)"
          currentUrl={branding?.favicon_url}
        />
      </div>

      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
        <h3 className="text-blue-400 font-bold mb-2">💡 Logo Tips:</h3>
        <ul className="text-gray-400 text-sm space-y-1">
          <li>• <strong>Main Logo:</strong> PNG with transparent background (recommended 200x50px)</li>
          <li>• <strong>Light/Dark:</strong> Use when your logo needs different versions for different backgrounds</li>
          <li>• <strong>Footer Logo:</strong> Can be same as main or a variation</li>
          <li>• <strong>Favicon:</strong> Small square icon, 16x16, 32x32, or 64x64 pixels</li>
          <li>• <strong>Format:</strong> PNG recommended for transparency, JPG for photos</li>
        </ul>
      </div>
    </div>
  );
};

// Customers Management Tab
const CustomersTab = ({ currentTheme }) => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/customers`, {
        headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers:', error);
      toast({ title: "Error", description: "Failed to load customers", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerOrders = async (customerId) => {
    try {
      const response = await axios.get(`/api/customers/${customerId}/orders`, {
        headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
      });
      setCustomerOrders(response.data);
    } catch (error) {
      console.error('Failed to load customer orders:', error);
    }
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    loadCustomerOrders(customer.id);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-gray-600 border-t-white rounded-full animate-spin"></div>
        <p className="text-gray-400 mt-4">Loading customers...</p>
      </div>
    );
  }

  if (selectedCustomer && customerOrders) {
    return (
      <div className="space-y-6">
        <Button
          onClick={() => {
            setSelectedCustomer(null);
            setCustomerOrders(null);
          }}
          variant="outline"
          className="text-white border-white/20 hover:bg-white/10"
        >
          ← Back to Customers
        </Button>

        <div className="bg-black/50 rounded-xl p-6 border border-white/10">
          <h2 className="text-2xl font-bold text-white mb-4">{selectedCustomer.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <p className="text-gray-400 text-sm">Email</p>
              <p className="text-white font-bold">{selectedCustomer.email}</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total Purchases</p>
              <p className="text-white font-bold">{selectedCustomer.total_purchases}</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total Spent</p>
              <p className="text-white font-bold">${selectedCustomer.total_spent.toFixed(2)}</p>
            </div>
          </div>

          {selectedCustomer.discount_codes_used && selectedCustomer.discount_codes_used.length > 0 && (
            <div className="mb-6">
              <p className="text-gray-400 text-sm mb-2">Discount Codes Used</p>
              <div className="flex gap-2 flex-wrap">
                {selectedCustomer.discount_codes_used.map((code, i) => (
                  <span key={i} className="px-3 py-1 bg-green-500/20 text-green-400 rounded-lg text-sm">
                    {code}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <h3 className="text-xl font-bold text-white mb-4">Order History ({customerOrders.orders.length})</h3>
          {customerOrders.orders.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <ShoppingBag className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No orders yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {customerOrders.orders.map(order => (
                <div key={order.id} className="bg-black/50 rounded-xl p-4 border border-white/10">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-white font-bold">${order.amount}</p>
                      <p className="text-gray-400 text-sm">
                        {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-lg text-sm ${
                      order.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                      order.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {order.payment_status}
                    </span>
                  </div>
                  {order.items && order.items.length > 0 && (
                    <div className="text-gray-400 text-sm">
                      {order.items.length} item(s)
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Customers ({customers.length})</h2>
          <p className="text-gray-400 text-sm">View customer information and purchase history</p>
        </div>
      </div>

      {customers.length === 0 ? (
        <div className="text-center py-12 bg-black/50 rounded-xl border border-white/10">
          <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No customers yet. Customers are created automatically when they make purchases.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {customers.map(customer => (
            <div key={customer.id} className="bg-black/50 rounded-xl p-4 border border-white/10 hover:border-white/20 transition-all">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-white font-bold text-lg">{customer.name}</h3>
                  <p className="text-gray-400 text-sm">{customer.email}</p>
                  <div className="flex gap-4 mt-2">
                    <span className="text-gray-400 text-sm">
                      {customer.total_purchases} purchase{customer.total_purchases !== 1 ? 's' : ''}
                    </span>
                    <span className="text-white font-bold text-sm">
                      ${customer.total_spent.toFixed(2)} spent
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => handleViewCustomer(customer)}
                  style={{ backgroundColor: currentTheme.primary }}
                  className="text-black font-bold"
                  size="sm"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View Details
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Placeholder tabs (simplified for now)
const MerchTab = ({ currentTheme }) => {
  const [merch, setMerch] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingMerch, setEditingMerch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'apparel',
    sizes: '',
    colors: '',
    inStock: true,
    image_file: null
  });

  useEffect(() => {
    loadMerch();
  }, []);

  const loadMerch = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/merch`);
      setMerch(response.data);
    } catch (error) {
      console.error('Failed to load merch:', error);
      toast({ title: "Error", description: "Failed to load merchandise", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('description', formData.description);
      data.append('price', formData.price);
      data.append('category', formData.category);
      data.append('sizes', formData.sizes);
      data.append('colors', formData.colors);
      data.append('inStock', formData.inStock);
      if (formData.image_file) {
        data.append('image_file', formData.image_file);
      }

      await axios.post(`/api/merch`, data, {
        headers: {
          'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm',
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({ title: "Success", description: "Merchandise created successfully!" });
      setShowForm(false);
      resetForm();
      loadMerch();
    } catch (error) {
      console.error('Failed to create merch:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to create merchandise", 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (merchId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await axios.delete(`/api/merch/${merchId}`, {
        headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
      });
      toast({ title: "Success", description: "Merchandise deleted successfully!" });
      loadMerch();
    } catch (error) {
      console.error('Failed to delete merch:', error);
      toast({ title: "Error", description: "Failed to delete merchandise", variant: "destructive" });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'apparel',
      sizes: '',
      colors: '',
      inStock: true,
      image_file: null
    });
    setEditingMerch(null);
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Add Merchandise</h2>
          <Button onClick={() => { setShowForm(false); resetForm(); }} variant="outline" className="text-white">
            Cancel
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
                required
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
                required
              />
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-2 block">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
              >
                <option value="apparel">Apparel</option>
                <option value="accessories">Accessories</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-sm mb-2 block">In Stock</label>
              <select
                value={formData.inStock}
                onChange={(e) => setFormData({...formData, inStock: e.target.value === 'true'})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Sizes (comma-separated, e.g., S,M,L,XL)</label>
              <input
                type="text"
                value={formData.sizes}
                onChange={(e) => setFormData({...formData, sizes: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
                placeholder="S,M,L,XL"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Colors (comma-separated)</label>
              <input
                type="text"
                value={formData.colors}
                onChange={(e) => setFormData({...formData, colors: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
                placeholder="Black,White,Red"
              />
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-2 block">Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setFormData({...formData, image_file: e.target.files[0]})}
              className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            style={{ backgroundColor: currentTheme.primary }}
            className="w-full text-black font-bold"
          >
            {loading ? 'Creating...' : 'Create Merchandise'}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Merchandise</h2>
        <Button
          onClick={() => setShowForm(true)}
          style={{ backgroundColor: currentTheme.primary }}
          className="text-black font-bold"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Merch
        </Button>
      </div>

      {loading && <div className="text-center text-gray-400 py-8">Loading...</div>}

      {!loading && merch.length === 0 && (
        <div className="text-center text-gray-400 py-12">
          <Shirt className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>No merchandise yet. Click "Add Merch" to create one.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {merch.map(item => (
          <div key={item.id} className="bg-black/50 rounded-xl p-4 border border-white/10">
            <img
              src={`${BACKEND_URL}${item.imageUrl}`}
              alt={item.name}
              className="w-full h-48 object-cover rounded-lg mb-4"
            />
            <h3 className="text-white font-bold text-lg mb-2">{item.name}</h3>
            <p className="text-gray-400 text-sm mb-2 line-clamp-2">{item.description}</p>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-bold" style={{ color: currentTheme.primary }}>${item.price}</span>
              <span className={`px-2 py-1 rounded text-xs ${item.inStock ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                {item.inStock ? 'In Stock' : 'Out of Stock'}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => handleDelete(item.id)}
                variant="outline"
                className="flex-1 text-red-500 border-red-500/20 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SoundKitsTab = ({ currentTheme }) => {
  const [soundkits, setSoundkits] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    contents: '',
    fileSize: '',
    format: 'WAV (24-bit)',
    category: 'Drums',
    image_file: null,
    kit_file: null
  });

  useEffect(() => {
    loadSoundkits();
  }, []);

  const loadSoundkits = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/soundkits`);
      setSoundkits(response.data);
    } catch (error) {
      console.error('Failed to load sound kits:', error);
      toast({ title: "Error", description: "Failed to load sound kits", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('description', formData.description);
      data.append('price', formData.price);
      data.append('contents', formData.contents);
      data.append('fileSize', formData.fileSize);
      data.append('format', formData.format);
      data.append('category', formData.category);
      if (formData.image_file) {
        data.append('image_file', formData.image_file);
      }
      if (formData.kit_file) {
        data.append('kit_file', formData.kit_file);
      }

      await axios.post(`/api/soundkits`, data, {
        headers: {
          'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm',
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({ title: "Success", description: "Sound kit created successfully!" });
      setShowForm(false);
      resetForm();
      loadSoundkits();
    } catch (error) {
      console.error('Failed to create sound kit:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to create sound kit", 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (kitId) => {
    if (!window.confirm('Are you sure you want to delete this sound kit?')) return;

    try {
      await axios.delete(`/api/soundkits/${kitId}`, {
        headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
      });
      toast({ title: "Success", description: "Sound kit deleted successfully!" });
      loadSoundkits();
    } catch (error) {
      console.error('Failed to delete sound kit:', error);
      toast({ title: "Error", description: "Failed to delete sound kit", variant: "destructive" });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      contents: '',
      fileSize: '',
      format: 'WAV (24-bit)',
      category: 'Drums',
      image_file: null,
      kit_file: null
    });
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Add Sound Kit</h2>
          <Button onClick={() => { setShowForm(false); resetForm(); }} variant="outline" className="text-white">
            Cancel
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
                required
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
                required
              />
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-2 block">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
              >
                <option value="Drums">Drums</option>
                <option value="Melodies">Melodies</option>
                <option value="Vocals">Vocals</option>
                <option value="FX">FX</option>
                <option value="Complete">Complete</option>
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Format</label>
              <input
                type="text"
                value={formData.format}
                onChange={(e) => setFormData({...formData, format: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
                placeholder="WAV (24-bit)"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm mb-2 block">File Size</label>
              <input
                type="text"
                value={formData.fileSize}
                onChange={(e) => setFormData({...formData, fileSize: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
                placeholder="250 MB"
                required
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Contents (comma-separated)</label>
              <input
                type="text"
                value={formData.contents}
                onChange={(e) => setFormData({...formData, contents: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
                placeholder="808s,Kicks,Snares"
              />
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-2 block">Cover Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setFormData({...formData, image_file: e.target.files[0]})}
              className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
              required
            />
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-2 block">Kit File (ZIP)</label>
            <input
              type="file"
              accept=".zip"
              onChange={(e) => setFormData({...formData, kit_file: e.target.files[0]})}
              className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            style={{ backgroundColor: currentTheme.primary }}
            className="w-full text-black font-bold"
          >
            {loading ? 'Creating...' : 'Create Sound Kit'}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Sound Kits</h2>
        <Button
          onClick={() => setShowForm(true)}
          style={{ backgroundColor: currentTheme.primary }}
          className="text-black font-bold"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Sound Kit
        </Button>
      </div>

      {loading && <div className="text-center text-gray-400 py-8">Loading...</div>}

      {!loading && soundkits.length === 0 && (
        <div className="text-center text-gray-400 py-12">
          <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>No sound kits yet. Click "Add Sound Kit" to create one.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {soundkits.map(kit => (
          <div key={kit.id} className="bg-black/50 rounded-xl p-4 border border-white/10">
            <img
              src={`${BACKEND_URL}${kit.imageUrl}`}
              alt={kit.name}
              className="w-full h-48 object-cover rounded-lg mb-4"
            />
            <h3 className="text-white font-bold text-lg mb-2">{kit.name}</h3>
            <p className="text-gray-400 text-sm mb-2 line-clamp-2">{kit.description}</p>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-bold" style={{ color: currentTheme.primary }}>${kit.price}</span>
              <span className="text-gray-400 text-xs">{kit.fileSize}</span>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => handleDelete(kit.id)}
                variant="outline"
                className="flex-1 text-red-500 border-red-500/20 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AlbumsTab = ({ currentTheme }) => {
  const [albums, setAlbums] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // { trackIndex: 0-100 }
  const [bulkUploading, setBulkUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    year: new Date().getFullYear(),
    description: '',
    trackList: [{ title: '', duration: '' }],
    spotifyUrl: '',
    appleMusicUrl: '',
    youtubeUrl: '',
    soundcloudUrl: '',
    cover_file: null
  });

  useEffect(() => {
    loadAlbums();
  }, []);

  const loadAlbums = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/albums`);
      setAlbums(response.data);
    } catch (error) {
      console.error('Failed to load albums:', error);
      toast({ title: "Error", description: "Failed to load albums", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (album) => {
    setEditingAlbum(album);
    setFormData({
      title: album.title,
      year: album.year,
      description: album.description || '',
      trackList: album.tracks?.length ? album.tracks : [{ title: '', duration: '' }],
      spotifyUrl: album.streamingLinks?.spotify || '',
      appleMusicUrl: album.streamingLinks?.appleMusic || '',
      youtubeUrl: album.streamingLinks?.youtube || '',
      soundcloudUrl: album.streamingLinks?.soundcloud || '',
      cover_file: null
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!editingAlbum && !formData.cover_file) {
        toast({ title: "Error", description: "Please upload a cover image", variant: "destructive" });
        setLoading(false);
        return;
      }

      // Build tracks JSON from trackList
      const validTracks = formData.trackList.filter(t => t.title.trim());
      const tracksJson = JSON.stringify(validTracks.map(t => ({ title: t.title.trim(), duration: t.duration.trim() })));

      // Build streaming links JSON
      const links = {};
      if (formData.spotifyUrl.trim()) links.spotify = formData.spotifyUrl.trim();
      if (formData.appleMusicUrl.trim()) links.appleMusic = formData.appleMusicUrl.trim();
      if (formData.youtubeUrl.trim()) links.youtube = formData.youtubeUrl.trim();
      if (formData.soundcloudUrl.trim()) links.soundcloud = formData.soundcloudUrl.trim();

      const data = new FormData();
      data.append('title', formData.title);
      data.append('year', formData.year);
      data.append('tracks', tracksJson);
      if (formData.description) data.append('description', formData.description);
      if (Object.keys(links).length > 0) data.append('streamingLinks', JSON.stringify(links));
      if (formData.cover_file) data.append('cover_file', formData.cover_file);

      if (editingAlbum) {
        await axios.put(`/api/albums/${editingAlbum.id}`, data, {
          headers: {
            'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm',
            'Content-Type': 'multipart/form-data'
          }
        });
        toast({ title: "Success", description: "Album updated successfully!" });
      } else {
        data.append('cover_file', formData.cover_file);
        await axios.post(`/api/albums`, data, {
          headers: {
            'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm',
            'Content-Type': 'multipart/form-data'
          }
        });
        toast({ title: "Success", description: "Album created successfully!" });
      }

      setShowForm(false);
      setEditingAlbum(null);
      resetForm();
      loadAlbums();
    } catch (error) {
      console.error('Failed to save album:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to save album", 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (albumId) => {
    if (!window.confirm('Are you sure you want to delete this album?')) return;

    try {
      await axios.delete(`/api/albums/${albumId}`, {
        headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
      });
      toast({ title: "Success", description: "Album deleted successfully!" });
      loadAlbums();
    } catch (error) {
      console.error('Failed to delete album:', error);
      toast({ title: "Error", description: "Failed to delete album", variant: "destructive" });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      year: new Date().getFullYear(),
      description: '',
      trackList: [{ title: '', duration: '' }],
      spotifyUrl: '',
      appleMusicUrl: '',
      youtubeUrl: '',
      soundcloudUrl: '',
      cover_file: null
    });
  };

  const uploadTrackAudio = async (albumId, trackIndex, file) => {
    if (!file) return;
    const data = new FormData();
    data.append('audio_file', file);
    setUploadProgress(prev => ({ ...prev, [trackIndex]: 0 }));
    try {
      await axios.post(`/api/albums/${albumId}/tracks/${trackIndex}/upload-audio`, data, {
        headers: {
          'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm',
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(prev => ({ ...prev, [trackIndex]: pct }));
        }
      });
      setUploadProgress(prev => ({ ...prev, [trackIndex]: 100 }));
      toast({ title: "✅ Uploaded", description: `Track ${trackIndex + 1} audio uploaded!` });
      const res = await axios.get(`/api/albums/${albumId}`);
      const updatedTracks = res.data.tracks || [];
      setFormData(prev => ({ ...prev, trackList: updatedTracks }));
      setTimeout(() => setUploadProgress(prev => { const n = {...prev}; delete n[trackIndex]; return n; }), 2000);
    } catch (err) {
      setUploadProgress(prev => { const n = {...prev}; delete n[trackIndex]; return n; });
      toast({ title: "Error", description: "Failed to upload audio", variant: "destructive" });
    }
  };

  const bulkUploadTracks = async (albumId, files) => {
    if (!files || !files.length) return;
    setBulkUploading(true);
    const fileArr = Array.from(files);

    // Sort files by leading number in filename (01-track.flac, 02-track.flac, etc.)
    const getLeadingNum = (name) => {
      const m = name.match(/^(\d+)/);
      return m ? parseInt(m[1]) : 9999;
    };
    fileArr.sort((a, b) => {
      const na = getLeadingNum(a.name);
      const nb = getLeadingNum(b.name);
      if (na !== nb) return na - nb;
      return a.name.localeCompare(b.name);
    });

    // Try to match each file to a track by number prefix or name similarity
    const tracks = formData.trackList;
    const matched = []; // [{trackIndex, file}]
    const usedTrackIndices = new Set();

    for (const file of fileArr) {
      const baseName = file.name.replace(/\.[^/.]+$/, '').toLowerCase();
      const leadNum = getLeadingNum(file.name);
      let bestIndex = -1;

      // 1. Match by leading number (01 → track index 0, 02 → index 1, etc.)
      if (leadNum !== 9999) {
        const idx = leadNum - 1;
        if (idx >= 0 && idx < tracks.length && !usedTrackIndices.has(idx)) {
          bestIndex = idx;
        }
      }

      // 2. If no number match, try fuzzy name match
      if (bestIndex === -1) {
        let bestScore = 0;
        tracks.forEach((track, idx) => {
          if (usedTrackIndices.has(idx)) return;
          const trackName = track.title.toLowerCase().replace(/[^a-z0-9]/g, '');
          const fileName = baseName.replace(/^\d+[-_\s]*/, '').replace(/[^a-z0-9]/g, '');
          // Count matching characters
          let score = 0;
          const shorter = fileName.length < trackName.length ? fileName : trackName;
          for (let c of shorter) {
            if (trackName.includes(c) && fileName.includes(c)) score++;
          }
          if (score > bestScore) { bestScore = score; bestIndex = idx; }
        });
      }

      // 3. Fallback: next available slot
      if (bestIndex === -1) {
        for (let i = 0; i < tracks.length; i++) {
          if (!usedTrackIndices.has(i)) { bestIndex = i; break; }
        }
      }

      if (bestIndex !== -1) {
        usedTrackIndices.add(bestIndex);
        matched.push({ trackIndex: bestIndex, file });
      }
    }

    // Sort by trackIndex for clean sequential uploads
    matched.sort((a, b) => a.trackIndex - b.trackIndex);

    // Show match preview in toast
    const preview = matched.slice(0, 3).map(m => `${m.trackIndex + 1}. ${tracks[m.trackIndex]?.title}`).join(', ');
    toast({ title: `📁 Matched ${matched.length} files`, description: preview + (matched.length > 3 ? '...' : '') });

    for (const { trackIndex, file } of matched) {
      await uploadTrackAudio(albumId, trackIndex, file);
    }

    setBulkUploading(false);
    toast({ title: "🔥 Bulk Upload Done", description: `${matched.length} tracks uploaded! Pro tip: name files 01-trackname.mp3 for perfect auto-match.` });
  };

  const addTrack = () => {
    setFormData({ ...formData, trackList: [...formData.trackList, { title: '', duration: '' }] });
  };

  const removeTrack = (index) => {
    const updated = formData.trackList.filter((_, i) => i !== index);
    setFormData({ ...formData, trackList: updated.length ? updated : [{ title: '', duration: '' }] });
  };

  const updateTrack = (index, field, value) => {
    const updated = [...formData.trackList];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, trackList: updated });
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">{editingAlbum ? 'Edit Album' : 'Add Album'}</h2>
          <Button onClick={() => { setShowForm(false); setEditingAlbum(null); resetForm(); }} variant="outline" className="text-white">
            Cancel
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
                required
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Year</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
                required
              />
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-2 block">Description (Optional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-gray-400 text-sm">Tracks {editingAlbum && <span className="text-xs text-yellow-400">(Save album first, then upload audio)</span>}</label>
              {editingAlbum && (
                <label className={`cursor-pointer text-xs px-3 py-1 rounded-lg font-bold transition-all ${bulkUploading ? 'opacity-50 cursor-not-allowed bg-gray-700 text-gray-400' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}>
                  {bulkUploading ? '⏳ Uploading...' : '📁 Bulk Upload All Tracks'}
                  <input
                    type="file"
                    accept="audio/*"
                    multiple
                    disabled={bulkUploading}
                    className="hidden"
                    onChange={(e) => bulkUploadTracks(editingAlbum.id, e.target.files)}
                  />
                </label>
              )}
            </div>
            {editingAlbum && (
              <p className="text-xs text-gray-500 mb-3">💡 Bulk upload: select all MP3s at once — they'll match to tracks in order. Or upload individually per track below.</p>
            )}
            <div className="space-y-3">
              {formData.trackList.map((track, idx) => (
                <div key={idx} className="bg-black/30 rounded-lg p-3 border border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-gray-500 text-sm w-6">{idx + 1}.</span>
                    <input
                      type="text"
                      value={track.title}
                      onChange={(e) => updateTrack(idx, 'title', e.target.value)}
                      placeholder="Track title"
                      className="flex-1 px-3 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm"
                    />
                    <input
                      type="text"
                      value={track.duration}
                      onChange={(e) => updateTrack(idx, 'duration', e.target.value)}
                      placeholder="3:45"
                      className="w-20 px-3 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm text-center"
                    />
                    <button
                      type="button"
                      onClick={() => removeTrack(idx)}
                      className="text-red-400 hover:text-red-300 text-sm px-2"
                    >✕</button>
                  </div>
                  {editingAlbum && (
                    <div className="pl-6 space-y-2">
                      <div className="flex items-center gap-3">
                        {track.audioUrl ? (
                          <span className="text-green-400 text-xs flex items-center gap-1">✅ Audio ready</span>
                        ) : (
                          <span className="text-gray-500 text-xs">🎵 No audio yet</span>
                        )}
                        <label className="cursor-pointer text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-gray-300 transition-all">
                          {track.audioUrl ? 'Replace' : 'Upload MP3'}
                          <input
                            type="file"
                            accept="audio/*"
                            className="hidden"
                            onChange={(e) => e.target.files[0] && uploadTrackAudio(editingAlbum.id, idx, e.target.files[0])}
                          />
                        </label>
                      </div>
                      {uploadProgress[idx] !== undefined && (
                        <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${uploadProgress[idx]}%`,
                              backgroundColor: uploadProgress[idx] === 100 ? '#22c55e' : '#3b82f6'
                            }}
                          />
                        </div>
                      )}
                      {uploadProgress[idx] !== undefined && (
                        <p className="text-xs text-blue-400">{uploadProgress[idx] === 100 ? '✅ Done!' : `Uploading... ${uploadProgress[idx]}%`}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addTrack}
                className="text-sm px-3 py-1 rounded-lg border border-dashed border-white/20 text-gray-400 hover:text-white hover:border-white/40 transition-all"
              >+ Add Track</button>
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-2 block">Streaming Links (optional)</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                type="url"
                value={formData.spotifyUrl}
                onChange={(e) => setFormData({...formData, spotifyUrl: e.target.value})}
                placeholder="Spotify URL"
                className="px-3 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm"
              />
              <input
                type="url"
                value={formData.appleMusicUrl}
                onChange={(e) => setFormData({...formData, appleMusicUrl: e.target.value})}
                placeholder="Apple Music URL"
                className="px-3 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm"
              />
              <input
                type="url"
                value={formData.youtubeUrl}
                onChange={(e) => setFormData({...formData, youtubeUrl: e.target.value})}
                placeholder="YouTube URL"
                className="px-3 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm"
              />
              <input
                type="url"
                value={formData.soundcloudUrl}
                onChange={(e) => setFormData({...formData, soundcloudUrl: e.target.value})}
                placeholder="SoundCloud URL"
                className="px-3 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-2 block">Cover Image {editingAlbum ? '(leave blank to keep current)' : '*'}</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setFormData({...formData, cover_file: e.target.files[0]})}
              className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
              required={!editingAlbum}
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            style={{ backgroundColor: currentTheme.primary }}
            className="w-full text-black font-bold"
          >
            {loading ? 'Saving...' : editingAlbum ? 'Update Album' : 'Create Album'}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Albums</h2>
        <Button
          onClick={() => setShowForm(true)}
          style={{ backgroundColor: currentTheme.primary }}
          className="text-black font-bold"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Album
        </Button>
      </div>

      {loading && <div className="text-center text-gray-400 py-8">Loading...</div>}

      {!loading && albums.length === 0 && (
        <div className="text-center text-gray-400 py-12">
          <Album className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>No albums yet. Click "Add Album" to create one.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {albums.map(album => (
          <div key={album.id} className="bg-black/50 rounded-xl p-4 border border-white/10">
            <img
              src={`${BACKEND_URL}${album.coverUrl}`}
              alt={album.title}
              className="w-full h-48 object-cover rounded-lg mb-4"
            />
            <h3 className="text-white font-bold text-lg mb-1">{album.title}</h3>
            <p className="text-gray-400 text-sm mb-3">{album.year} • {album.tracks?.length || 0} tracks</p>
            {album.description && (
              <p className="text-gray-400 text-sm mb-3 line-clamp-2">{album.description}</p>
            )}
            <div className="flex gap-2">
              <Button
                onClick={() => handleEdit(album)}
                variant="outline"
                className="flex-1 text-blue-400 border-blue-400/20 hover:bg-blue-400/10"
              >
                ✏️ Edit
              </Button>
              <Button
                onClick={() => handleDelete(album.id)}
                variant="outline"
                className="flex-1 text-red-500 border-red-500/20 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MessagesTab = ({ currentTheme }) => {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const response = await axios.get(`/api/messages`, {
        headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white mb-4">Customer Messages</h2>
      {messages.length === 0 ? (
        <div className="text-center text-gray-400 py-12">
          <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>No messages yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map(msg => (
            <div key={msg.id} className="bg-black/50 rounded-xl p-4 border border-white/10">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="text-white font-bold">{msg.name}</h3>
                  <p className="text-gray-400 text-sm">{msg.email}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${
                  msg.status === 'unread' ? 'bg-yellow-500/20 text-yellow-500' :
                  msg.status === 'replied' ? 'bg-green-500/20 text-green-500' :
                  'bg-blue-500/20 text-blue-500'
                }`}>
                  {msg.status}
                </span>
              </div>
              <p className="text-white font-semibold mb-2">{msg.subject}</p>
              <p className="text-gray-400 text-sm">{msg.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EmailTab = ({ currentTheme }) => {
  const [emailData, setEmailData] = useState({
    to_email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await axios.post(`/api/admin/email/send`, emailData, {
        headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
      });
      toast({ title: "Success", description: "Email sent successfully!" });
      setEmailData({ to_email: '', subject: '', message: '' });
    } catch (error) {
      toast({ title: "Error", description: "Failed to send email", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white mb-4">Send Email</h2>
      <form onSubmit={handleSend} className="space-y-4">
        <div>
          <label className="text-gray-400 text-sm mb-2 block">To Email</label>
          <input
            type="email"
            value={emailData.to_email}
            onChange={(e) => setEmailData({...emailData, to_email: e.target.value})}
            className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
            required
          />
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-2 block">Subject</label>
          <input
            type="text"
            value={emailData.subject}
            onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
            className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
            required
          />
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-2 block">Message</label>
          <textarea
            value={emailData.message}
            onChange={(e) => setEmailData({...emailData, message: e.target.value})}
            rows={6}
            className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
            required
          />
        </div>
        <Button
          type="submit"
          disabled={sending}
          style={{ backgroundColor: currentTheme.primary }}
          className="w-full text-black font-bold"
        >
          <Send className="w-4 h-4 mr-2" />
          {sending ? 'Sending...' : 'Send Email'}
        </Button>
      </form>
    </div>
  );
};

const DiscountsTab = ({ currentTheme }) => {
  const [discounts, setDiscounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_percent: '',
    one_time_use: true,
    max_uses: '',
    expires_at: ''
  });

  useEffect(() => {
    loadDiscounts();
  }, []);

  const loadDiscounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/discounts`, {
        headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
      });
      setDiscounts(response.data);
    } catch (error) {
      console.error('Failed to load discounts:', error);
      toast({ title: "Error", description: "Failed to load discount codes", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        code: formData.code.toUpperCase(),
        discount_percent: parseFloat(formData.discount_percent),
        one_time_use: formData.one_time_use,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
      };

      await axios.post(`/api/discounts`, payload, {
        headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
      });

      toast({ title: "Success", description: "Discount code created successfully!" });
      setShowForm(false);
      resetForm();
      loadDiscounts();
    } catch (error) {
      console.error('Failed to create discount:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to create discount code", 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm('Are you sure you want to delete this discount code?')) return;

    try {
      await axios.delete(`/api/discounts/${code}`, {
        headers: { 'X-Admin-Password': process.env.REACT_APP_ADMIN_PASSWORD || 'advocate2026prizm' }
      });
      toast({ title: "Success", description: "Discount code deleted successfully!" });
      loadDiscounts();
    } catch (error) {
      console.error('Failed to delete discount:', error);
      toast({ title: "Error", description: "Failed to delete discount code", variant: "destructive" });
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_percent: '',
      one_time_use: true,
      max_uses: '',
      expires_at: ''
    });
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Create Discount Code</h2>
          <Button onClick={() => { setShowForm(false); resetForm(); }} variant="outline" className="text-white">
            Cancel
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Code</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white uppercase"
                placeholder="SUMMER2024"
                required
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Discount Percent (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.discount_percent}
                onChange={(e) => setFormData({...formData, discount_percent: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Max Uses (Optional)</label>
              <input
                type="number"
                min="1"
                value={formData.max_uses}
                onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
                placeholder="Leave empty for unlimited"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Expires At (Optional)</label>
              <input
                type="datetime-local"
                value={formData.expires_at}
                onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-gray-400">
              <input
                type="checkbox"
                checked={formData.one_time_use}
                onChange={(e) => setFormData({...formData, one_time_use: e.target.checked})}
                className="w-4 h-4"
              />
              <span className="text-sm">One-time use per email</span>
            </label>
          </div>

          <Button
            type="submit"
            disabled={loading}
            style={{ backgroundColor: currentTheme.primary }}
            className="w-full text-black font-bold"
          >
            {loading ? 'Creating...' : 'Create Discount Code'}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Discount Codes</h2>
        <Button
          onClick={() => setShowForm(true)}
          style={{ backgroundColor: currentTheme.primary }}
          className="text-black font-bold"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Code
        </Button>
      </div>

      {loading && <div className="text-center text-gray-400 py-8">Loading...</div>}

      {!loading && discounts.length === 0 && (
        <div className="text-center text-gray-400 py-12">
          <Tag className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>No discount codes yet. Click "Create Code" to add one.</p>
        </div>
      )}

      <div className="space-y-3">
        {discounts.map(discount => (
          <div key={discount.id} className="bg-black/50 rounded-xl p-4 border border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-white font-bold text-xl">{discount.code}</h3>
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-bold"
                    style={{ backgroundColor: `${currentTheme.primary}30`, color: currentTheme.primary }}
                  >
                    {discount.discount_percent}% OFF
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    discount.active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                  }`}>
                    {discount.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex gap-4 text-sm text-gray-400">
                  <span>Uses: {discount.use_count}{discount.max_uses ? `/${discount.max_uses}` : ''}</span>
                  {discount.one_time_use && <span>• One-time per email</span>}
                  {discount.expires_at && (
                    <span>• Expires: {new Date(discount.expires_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              <Button
                onClick={() => handleDelete(discount.code)}
                variant="outline"
                className="text-red-500 border-red-500/20 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const OrdersTab = ({ currentTheme }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/payments/transactions`);
      // Sort by date, newest first
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setOrders(sorted);
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast({ title: "Error", description: "Failed to load orders", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'paid':
        return 'bg-green-500/20 text-green-500';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-500';
      case 'failed':
        return 'bg-red-500/20 text-red-500';
      default:
        return 'bg-gray-500/20 text-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Order History</h2>
        <Button
          onClick={loadOrders}
          variant="outline"
          className="text-white border-white/20"
        >
          Refresh
        </Button>
      </div>

      {loading && <div className="text-center text-gray-400 py-8">Loading...</div>}

      {!loading && orders.length === 0 && (
        <div className="text-center text-gray-400 py-12">
          <ShoppingBag className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>No orders yet</p>
        </div>
      )}

      <div className="space-y-3">
        {orders.map(order => (
          <div key={order.id} className="bg-black/50 rounded-xl p-4 border border-white/10">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-bold">Order #{order.session_id.slice(-8)}</h3>
                  <span className={`px-2 py-1 rounded text-xs ${getStatusColor(order.payment_status)}`}>
                    {order.payment_status}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">
                  {new Date(order.created_at).toLocaleString()}
                </p>
              </div>
              <div className="text-right">
                <div className="text-white font-bold text-xl" style={{ color: currentTheme.primary }}>
                  ${order.amount.toFixed(2)}
                </div>
                <p className="text-gray-400 text-xs uppercase">{order.currency}</p>
              </div>
            </div>

            {order.items && order.items.length > 0 && (
              <div className="border-t border-white/10 pt-3">
                <p className="text-gray-400 text-sm mb-2">Items:</p>
                <div className="space-y-1">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-white">
                        {item.name} 
                        <span className="text-gray-500 ml-2">({item.type})</span>
                      </span>
                      <span className="text-gray-400">
                        ${item.price} x {item.quantity}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {order.metadata?.discount_code && (
              <div className="mt-2 text-sm">
                <span className="text-gray-400">Discount Code: </span>
                <span className="text-white font-bold">{order.metadata.discount_code}</span>
                <span className="text-gray-400 ml-2">
                  ({order.metadata.discount_percent}% off)
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminDashboard;
